/**
 * AffiliateSourceCapture — the `/ace` and `/cee` campaign entry points.
 *
 * These paths exist only to attribute a visitor: the slug is stored, then the
 * visitor is handed straight to the application form where it pre-fills "How did
 * you hear about us". Rendering nothing but a status line avoids a flash of
 * content on a page nobody is meant to read.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { STORAGE_KEYS } from '@wlb/shared/constants';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { storage } from '@wlb/shared/platform/storage';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';

interface AffiliateSourceCaptureProps {
  /** Campaign slug this route stands for, from `AFFILIATE_SOURCE_PATHS`. */
  source: string;
}

export function AffiliateSourceCapture({ source }: AffiliateSourceCaptureProps) {
  const { t } = useTranslation();
  const [stored, setStored] = useState(false);

  useEffect(() => {
    let active = true;
    storage
      .setItem(STORAGE_KEYS.AFFILIATE_SOURCE, source)
      .catch(() => undefined)
      .finally(() => {
        if (active) setStored(true);
      });
    return () => {
      active = false;
    };
  }, [source]);

  // Redirect only once the slug is committed, or the form would read it back empty.
  if (stored) {
    return <Navigate to={AFFILIATE_ROUTES.SIGN_UP} replace />;
  }
  return (
    <div className="app-loader" role="status">
      {t('common.loading')}
    </div>
  );
}
